import React, { useState } from 'react'
import { AlertCircle, CheckCircle, Heart, Users, Clock, Layers, TrendingUp } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import DownloadForm from '@/components/common/DownloadForm'

const CaseStudyVailHealth: React.FC = () => {
  const navigationStore = useNavigationStore()
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  
  const handleBookDemo = () => navigationStore.openDemoModal()
  const handleDownloadClick = () => setIsDownloadModalOpen(true)
  const handleCloseDownloadModal = () => setIsDownloadModalOpen(false)

  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How Vail Health Scaled COVID‑19 Testing & Vaccination with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">A consumer‑grade self‑scheduling portal reduced staff burden and handled 1,500–2,000 appointments per day at peak.</p>
              <div className="mt-8 flex gap-3">
                <button onClick={handleDownloadClick} className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition">Download Full Case Study</button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">vaxmanager</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">Configured as a consumer self‑scheduling portal with multi‑EHR integration for registration, scheduling, and lab orders.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">1,500–2,000/day</div>
              <div className="text-slate-600">Peak self‑scheduled appts</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Clock className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">5‑week launch</div>
              <div className="text-slate-600">From kickoff to live</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Layers className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Multi‑EHR</div>
              <div className="text-slate-600">Registration + orders</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <TrendingUp className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Lower no‑shows</div>
              <div className="text-slate-600">Automated reminders & reschedules</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">In a high‑demand resort region, manual registration and scheduling for hundreds of daily COVID‑19 tests overwhelmed staff and wasted time when patients didn’t show.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Manual EHR registration + appointment scheduling at multiple facilities</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Heavy administrative burden across fluctuating seasonal volumes</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>No‑shows caused lost time and unused capacity</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Manual intake & scheduling</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Multi‑site coordination</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Limited capacity visibility</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">No‑show risk</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">VaxManagerCX deployed as a standalone consumer portal with phase‑two integrations to Vail Health’s EHRs for automated registration, scheduling, and lab order processing.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Self‑service scheduling</div><div className="mt-1 text-slate-600">Patients schedule/manage their own testing and vaccine appointments.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Multi‑EHR integration</div><div className="mt-1 text-slate-600">Automates registration, appointments, and lab orders; reduces manual entry.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Rapid go‑live</div><div className="mt-1 text-slate-600">Portal launched in 5 weeks; integrations added in phase two.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Reminders & rescheduling</div><div className="mt-1 text-slate-600">Automated outreach lowers no‑show rates and maximizes capacity.</div></div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>1,500–2,000 daily</strong> self‑scheduled test & vaccine appointments at peak.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Lower administrative burden</strong> by shifting to self‑service and automation.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Fewer no‑shows</strong> through reminders and easy rescheduling.</span></div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-semibold">Human Impact</h2>
            <p className="mt-4 text-slate-700">Residents, part‑time locals, and tourists had faster access to testing and vaccines while staff focused on higher‑value tasks.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Less waiting and friction for patients and families.</span></li>
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Staff relief during peak seasonal surges.</span></li>
            </ul>
          </div>
          <aside className="rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-6">
            <blockquote className="text-lg leading-relaxed">“Shifting to a self‑service portal gave our teams time back and kept throughput high across multiple sites.”</blockquote>
            <div className="mt-3 text-slate-600">— Program Leadership, Vail Health</div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">A rapid, consumer‑grade deployment helped Vail Health manage extreme demand and build a scalable model for immunization & testing programs.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Speed to value</div><div className="text-slate-600 mt-1">Launched in ~5 weeks; integrations in phase two</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Scalable operations</div><div className="text-slate-600 mt-1">Handled 1.5k–2k appts/day across sites</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Better experience</div><div className="text-slate-600 mt-1">Less admin, faster access, clearer comms</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Ready to transform patient access and care coordination?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX can streamline complex workflows across your network.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />
      
      <DownloadForm
        isOpen={isDownloadModalOpen}
        onClose={handleCloseDownloadModal}
        paperTitle="Vail Health Case Study"
        pdfPath="/PDF/Detailed Case Studies/Eceptioinist_Case+Studies_Vail+Health.pdf"
      />
    </main>
  )
}

export default CaseStudyVailHealth


